import { DEPOSIT_REFUND_STATUS } from "../../constants/depositRefund";
import { isMoneyInput, parseDeductInput, round2 } from "./depositRefund.utils";

export type ReviewForm = {
  result: string;
  /** 'YYYY-MM-DD' — ค่าที่จะส่ง backend ไม่ใช่ข้อความ พ.ศ. ที่โชว์บนจอ */
  refund_date: string;
  doc_ref: string;
  note: string;
  /** ข้อความที่ผู้ใช้พิมพ์ (มี comma ได้) แปลงเป็นตัวเลขตอนส่ง */
  deduct_move_out: string;
  deduct_damage: string;
};

export type ReviewFormErrors = Partial<Record<keyof ReviewForm, string>>;

export const EMPTY_REVIEW_FORM: ReviewForm = {
  result: "",
  refund_date: "",
  doc_ref: "",
  note: "",
  deduct_move_out: "",
  deduct_damage: "",
};

/** ข้อความเดียวใช้ทั้ง 2 ช่อง ให้ผู้ใช้เห็นตัวอย่างที่กรอกได้ */
const MONEY_FORMAT_ERROR = "กรอกเป็นจำนวนเงิน เช่น 1,000 หรือ 1000.50";

type ReviewLimits = {
  /** ยอดตั้งต้นของแต่ละบรรทัด — ใช้เตือนเมื่อกรอกหักเกิน */
  moveOutBase: number;
  damageBase: number;
};

/**
 * แหล่งความจริงเดียวของกฎในฟอร์มพิจารณา — ใช้ทั้งปิดปุ่มยืนยันและขึ้น helperText ใต้ช่อง
 *
 * ทำไมต้องมี helperText ไม่ใช่แค่ปิดปุ่ม: ModalCustom หรี่ปุ่มด้วย opacity เท่านั้น
 * ไม่บอกเหตุผล ถ้าไม่โชว์ข้อความ ผู้ใช้จะกดปุ่มตายแล้วไม่รู้ว่าขาดอะไร
 *
 * เงื่อนไขตามผลการพิจารณา:
 *  - อนุมัติ    -> ต้องมีวันที่คืนเงิน + เลขที่เอกสารอ้างอิง (เป็นหลักฐานการจ่ายเงิน)
 *  - ไม่อนุมัติ -> ต้องมีเหตุผล เพราะเป็นคำสั่งที่เป็นผลร้ายต่อผู้ยื่น ตาม พ.ร.บ.วิธีปฏิบัติ
 *                 ราชการทางปกครอง 2539 ม.37 ที่กำหนดให้ต้องระบุเหตุผล
 *
 * ยอดหักไม่บังคับกรอก (ว่าง = ไม่หัก = คืนเต็มจำนวน) แต่กรอกเกินยอดตั้งต้นไม่ได้
 * — backend clamp ให้อยู่แล้ว แต่ถ้าไม่เตือนที่หน้าจอ ผู้ใช้จะไม่รู้ว่ายอดถูกตัด
 */
export const validateReviewForm = (
  form: ReviewForm,
  limits: ReviewLimits
): ReviewFormErrors => {
  const errors: ReviewFormErrors = {};

  if (!form.result) {
    errors.result = "กรุณาเลือกผลการพิจารณา";
    // ยังไม่เลือกผล ยังไม่รู้ว่าช่องไหนบังคับ จึงไม่ต้องตรวจต่อ
    return errors;
  }

  if (form.result === DEPOSIT_REFUND_STATUS.APPROVE) {
    if (!form.refund_date) errors.refund_date = "กรุณาระบุวันที่คืนเงิน";
    if (!form.doc_ref.trim()) errors.doc_ref = "กรุณาระบุเลขที่เอกสารอ้างอิง";

    // ตรวจรูปแบบก่อนตรวจยอด ไม่งั้น '12ab' จะถูก parse เป็น 0 แล้วผ่านไปเงียบๆ
    if (!isMoneyInput(form.deduct_move_out)) {
      errors.deduct_move_out = MONEY_FORMAT_ERROR;
    } else if (round2(parseDeductInput(form.deduct_move_out)) > limits.moveOutBase) {
      errors.deduct_move_out = "หักได้ไม่เกินยอดตั้งต้นของรายการนี้";
    }

    if (!isMoneyInput(form.deduct_damage)) {
      errors.deduct_damage = MONEY_FORMAT_ERROR;
    } else if (round2(parseDeductInput(form.deduct_damage)) > limits.damageBase) {
      errors.deduct_damage = "หักได้ไม่เกินยอดตั้งต้นของรายการนี้";
    }
  }

  if (form.result === DEPOSIT_REFUND_STATUS.NOT_APPROVE && !form.note.trim()) {
    errors.note = "กรุณาระบุเหตุผลที่ไม่อนุมัติ";
  }

  return errors;
};
